import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { HR } from 'foundations-components/transitional/components/HR';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { Image } from 'foundations-components/transitional/components/Image';
import { MediaObject } from 'foundations-components/transitional/components/MediaObject';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { SprocketIcon } from 'foundations-assets/icons/components/SprocketIcon';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ReviewRatingInput, ReviewTextInput } from 'trellis-ecosystem';
import { EcosystemReviewModal } from './EcosystemReviewModal';
import { POSITIVE_RATING_MINIMUM } from '../../constants/ReviewLimits';
import { PENDING } from '../../constants/UnreviewableReason';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const APP_SUBSCREEN = 'app review modal';
function getCompletedAnswerFields(review) {
  return Object.entries(review.content).filter(([field, value]) => field && !!value).map(([field]) => field);
}
export function AppReviewModal({
  appIconUrl,
  appName,
  editedReview,
  initialRating,
  isPublic,
  offeringId,
  onClose,
  onSuccess,
  onTrackInteraction,
  unreviewableReason
}) {
  const isEditing = !!editedReview;

  // Extract rating from URL query parameter if present
  const ratingFromQuery = useMemo(() => {
    if (typeof window === 'undefined') return undefined;
    const params = new URLSearchParams(window.location.search);
    const ratingParam = params.get('rating');
    if (ratingParam) {
      const rating = parseInt(ratingParam, 10);
      if (!isNaN(rating) && rating >= 1 && rating <= 5) {
        return rating;
      }
    }
    return undefined;
  }, []);

  // Use query param rating if present, otherwise use initialRating prop
  const effectiveInitialRating = ratingFromQuery !== null && ratingFromQuery !== void 0 ? ratingFromQuery : initialRating;
  let defaultReview;
  const emptyReview = {
    content: {},
    ratings: {
      OVERALL: effectiveInitialRating
    },
    tags: {}
  };
  try {
    const storedReview = JSON.parse(sessionStorage.getItem(`APP_${offeringId}_review`));
    // If rating query param is present, override sessionStorage rating
    if (ratingFromQuery !== undefined && storedReview) {
      defaultReview = Object.assign({}, storedReview, {
        ratings: Object.assign({}, storedReview.ratings, {
          OVERALL: ratingFromQuery
        })
      });
    } else {
      defaultReview = storedReview || emptyReview;
    }
  } catch (__error) {
    defaultReview = emptyReview;
  }
  const [overallRating, setOverallRating] = useState(defaultReview.ratings['OVERALL'] || undefined);
  const [completedAnswerFields, setCompletedAnswerFields] = useState(getCompletedAnswerFields(defaultReview));
  const [requireFields, setRequireFields] = useState(overallRating !== undefined && overallRating < POSITIVE_RATING_MINIMUM || !!defaultReview.content.TITLE || !!defaultReview.content.REVIEW || !!defaultReview.content.PROS || !!defaultReview.content.CONS);
  const validationSchema = {
    content: {
      TITLE: {
        required: requireFields
      },
      REVIEW: {
        required: requireFields
      }
    },
    ratings: {
      OVERALL: {
        required: true
      }
    }
  };
  const openReviewWizardTracked = useRef(false);
  useEffect(() => {
    if (!openReviewWizardTracked.current) {
      onTrackInteraction({
        action: isEditing ? 'open edit review wizard' : 'open review wizard',
        subscreen: APP_SUBSCREEN
      });
      openReviewWizardTracked.current = true;
    }
  }, [isEditing, onTrackInteraction]);
  const alertedAndTrackedUnreviewableReason = useRef(false);
  useEffect(() => {
    if (!isEditing && unreviewableReason && unreviewableReason !== `ReviewError.${PENDING}` && alertedAndTrackedUnreviewableReason.current === false) {
      onTrackInteraction({
        action: 'review wizard auto-dismissed',
        subscreen: APP_SUBSCREEN,
        value: unreviewableReason
      });
      FloatingAlertStore.addAlert({
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `marketplace-ui-reviews.error.${unreviewableReason}`
        }),
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-ui-reviews.error.ReviewError.GENERIC_UNREVIEWABLE"
        }),
        type: 'danger'
      });
      alertedAndTrackedUnreviewableReason.current = true;
    }
  }, [isEditing, onTrackInteraction, unreviewableReason]);
  const handleSuccess = useCallback(() => {
    onTrackInteraction({
      action: 'submit review',
      subscreen: APP_SUBSCREEN
    });
    sessionStorage.removeItem(`APP_${offeringId}_review`);
    onSuccess();
  }, [onSuccess, offeringId, onTrackInteraction]);
  const handleClose = useCallback(() => {
    onTrackInteraction({
      action: 'dismiss review wizard',
      subscreen: APP_SUBSCREEN
    });
    onClose();
  }, [onClose, onTrackInteraction]);
  const onChange = useCallback(review => {
    if (review.content.TITLE || review.content.REVIEW || review.content.PROS || review.content.CONS) {
      setRequireFields(true);
    } else {
      setRequireFields(review.ratings['OVERALL'] < POSITIVE_RATING_MINIMUM);
    }
    if (review.ratings['OVERALL'] !== overallRating) {
      onTrackInteraction({
        action: 'set overall rating',
        subscreen: APP_SUBSCREEN,
        value: review.ratings['OVERALL']
      });
      setOverallRating(review.ratings['OVERALL']);
    }
    const answerFields = getCompletedAnswerFields(review);
    answerFields.forEach(field => {
      if (!completedAnswerFields.includes(field)) {
        onTrackInteraction({
          action: 'add review answer',
          subscreen: APP_SUBSCREEN,
          value: field
        });
      }
    });
    setCompletedAnswerFields(answerFields);
    if (!isEditing) {
      sessionStorage.setItem(`APP_${offeringId}_review`, JSON.stringify(review));
    }
  }, [completedAnswerFields, isEditing, offeringId, onTrackInteraction, overallRating]);
  if (isPublic || !isEditing && unreviewableReason) {
    return null;
  }
  return /*#__PURE__*/_jsxs(EcosystemReviewModal, {
    defaultReview: defaultReview,
    editedReview: editedReview,
    width: 600,
    headerContent: /*#__PURE__*/_jsx(Text, {
      as: "h2",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `marketplace-ui-reviews.write.${isEditing ? 'edit' : 'new'}`
      })
    }),
    offeringId: offeringId,
    onChange: onChange,
    onClose: handleClose,
    onSuccess: handleSuccess,
    ratingSection: /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(View, {
        marginBlock: "200",
        children: /*#__PURE__*/_jsxs(TFlex, {
          direction: "row",
          align: "center",
          justify: "center",
          children: [/*#__PURE__*/_jsx(MediaObject, {
            align: "center",
            itemLeft: /*#__PURE__*/_jsx(Image, {
              alt: appName,
              "data-test-id": "app-wizard-header-image",
              src: appIconUrl,
              inlineSize: '40px',
              maxInlineSize: '40px',
              paddingBlockStart: "300"
            }),
            children: /*#__PURE__*/_jsx(Text, {
              as: "h7",
              margin: "0",
              paddingBlockStart: "300",
              children: appName
            })
          }), /*#__PURE__*/_jsx(Text, {
            as: "h7",
            marginInlineStart: "200",
            paddingBlockStart: "300",
            children: "+"
          }), /*#__PURE__*/_jsx(MediaObject, {
            align: "end",
            itemLeft: /*#__PURE__*/_jsx(IconHolder, {
              color: "fill-brand-default",
              icon: SprocketIcon,
              margin: "0",
              padding: "0",
              size: "900"
            }),
            spacing: "none",
            children: /*#__PURE__*/_jsx(Text, {
              as: "h7",
              margin: "0",
              marginBlockEnd: "400",
              children: "HubSpot"
            })
          })]
        })
      }), /*#__PURE__*/_jsx(View, {
        marginBlockEnd: "400",
        as: "span",
        children: /*#__PURE__*/_jsx(Text, {
          as: "h5",
          marginBlockEnd: "200",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-ui-reviews.wizard.app.header"
          })
        })
      }), /*#__PURE__*/_jsx(ReviewRatingInput, {
        fieldKey: "OVERALL",
        label: unescapedText('marketplace-ui-reviews.wizard.app.labels.OVERALL')
      })]
    }),
    showLegalCheckbox: true,
    reviewType: "APP",
    requireEditReason: true,
    validationSchema: validationSchema,
    children: [/*#__PURE__*/_jsx(ReviewTextInput, {
      fieldKey: "TITLE",
      label: unescapedText('marketplace-ui-reviews.wizard.app.labels.TITLE'),
      placeholder: unescapedText('marketplace-ui-reviews.wizard.app.placeholders.TITLE'),
      resize: false,
      rows: 1
    }), ['REVIEW', 'PROS', 'CONS'].map(axis => /*#__PURE__*/_jsx(ReviewTextInput, {
      fieldKey: axis,
      label: unescapedText(`marketplace-ui-reviews.wizard.app.labels.${axis}`),
      placeholder: unescapedText(`marketplace-ui-reviews.wizard.app.placeholders.${axis}`, {
        appName
      })
    }, axis)), /*#__PURE__*/_jsx(HR, {
      marginBlockStart: "800"
    }), /*#__PURE__*/_jsx(ReviewTextInput, {
      fieldKey: "FEEDBACK",
      label: unescapedText('marketplace-ui-reviews.wizard.app.labels.FEEDBACK'),
      placeholder: unescapedText('marketplace-ui-reviews.wizard.app.placeholders.FEEDBACK', {
        appName
      })
    })]
  });
}